import './console-silencer' // MUST be first — silences console before other imports
import React from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider } from "next-themes"
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import './index.css'
import './i18n'
import { installSecurityMonitoring } from './lib/security-monitoring'

// Clear chunk reload flag on successful app load
sessionStorage.removeItem('retry-lazy-refreshed');

// Initialize Sentry BEFORE React renders
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: __GIT_COMMIT_SHA__,

  // Send envelopes through our own origin instead of straight to
  // *.ingest.sentry.io, which content blockers kill client-side
  // (ERR_BLOCKED_BY_CLIENT). See api/monitoring.ts for the destination check
  // that keeps this from being an open relay.
  tunnel: '/api/monitoring',

  // Only enable in production (no noise during dev)
  enabled: import.meta.env.PROD,

  // Performance monitoring (10% of transactions)
  tracesSampleRate: 0.1,

  // Catch unhandled promise rejections
  integrations: [
    Sentry.browserTracingIntegration(),
  ],

  // Filter known benign errors
  ignoreErrors: [
    'AbortError: signal is aborted without reason',
    /Failed to fetch dynamically imported module/,
  ],
});

// Expose Sentry globally for console testing (production only)
if (import.meta.env.PROD) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- global window augmentation for Sentry
  (window as any).Sentry = Sentry;
}

// Browser-side security event monitoring (Phase A.5.1).
// CSP violations also POST directly to Sentry via `report-uri` in
// vercel.json — this is defense-in-depth + adds suspicious-URL +
// SRI-failure capture that report-uri doesn't cover.
installSecurityMonitoring();

// Force cache refresh - UI fix deployment
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <App />
    </ThemeProvider>
  </React.StrictMode>
);
